import React, { useRef, useEffect } from "react";
import ApexCharts from "apexcharts";

// helper to build colors conditionally
const getLabelColors = (labels, type) => {
  if (type === "type_distinct") {
    return labels.map((label) => {
      switch (label) {
        case "Draft": return "#39C0C3";
        case "Send to Client": return "#066fd1";
        case "Accepted": return "#74C163";
        case "Rejected": return "#FB3B52";
        case "Feedback": return "#FF9F0A";
        default: return "#999999";
      }
    });
  }

  if (type === "type_cbqa") {
    return labels.map((label) => {
      switch (label) {
        case "External Marketing": return "#39C0C3";
        case "Other": return "#D3D3D3";
        case "Consultant": return "#349FAA";
        case "Existing Client": return "#DAE254";
        case "Website": return "#A9CF38";
        case "Sales Prospect": return "#74C163";
        case "Event": return "#FF6B6B";
        case "Webinar": return "#4ECDC4";
        case "Internal": return "#45B7D1";
        case "Email": return "#96CEB4";
        default: return "#999999";
      }
    });
  }

  if (type === "type_audit_review") {
    return labels.map((label) => {
      switch (label) {
        case "Approved": return "#2fb344";
        case "Approved with Note": return "#f59f00";
        case "Reject": return "#e03131";
        default: return "#999999";
      }
    });
  }

  // fallback
  return labels.map(() => "#333333");
};

export default function DonutChart({
  series = [],
  labels = [],
  width = "100%",
  height = "350",
  legendPosition = "top", // "top" or "bottom"
  type = "type_cbqa",     // <-- new prop to control color scheme
  onDataPointClick,       // <-- callback for when a slice is clicked
  onDataPointRightClick,  // <-- callback for when a slice is right-clicked
}) {
  const chartRef = useRef(null);
  const rightClickHandledRef = useRef(false);

  useEffect(() => {
    const colors = getLabelColors(labels, type);
    const totalValue = series.reduce((a, b) => a + b, 0);

    const options = {
      series,
      labels,
      chart: { 
        type: "donut", 
        width, 
        height,
        zoom: { enabled: false },
        selection: { enabled: false },
        events: onDataPointClick ? {
          // Prevent ApexCharts from processing clicks internally
          click: function(event, chartContext, config) {
            // Do nothing - we handle clicks via React onClick
            // Add defensive checks to prevent ApexCharts internal errors
            try {
              if (event) {
                if (event.preventDefault) event.preventDefault();
                if (event.stopPropagation) event.stopPropagation();
              }
            } catch (error) {
              // Silently ignore errors to prevent breaking the chart
            }
          }
        } : {}
      },
      colors,
      legend: {
        show: true,
        position: legendPosition,
        horizontalAlign: "center",
        fontSize: "16px",
        marker: { show: false },
        // Keep legend interactive for hover highlight (updates donut center value),
        // but prevent default toggle/hide behavior on click.
        onItemHover: { highlightDataSeries: true },
        onItemClick: { toggleDataSeries: false },
        formatter: (label, opts) => {
          const color = opts.w.globals.colors[opts.seriesIndex];
          const value = series[opts.seriesIndex];

          if (legendPosition === "top") {
            return `
              <div class="legend-item legend-top">
                <div class="legend-header">
                  <span class="legend-dot" style="background:${color}"></span>
                  <span class="legend-label">${label}</span>
                </div>
                <div class="legend-value">${value}</div>
              </div>
            `;
          } else {
            return `
              <div class="legend-item legend-bottom">
                <span class="legend-dot" style="background:${color}"></span>
                <span class="legend-label">${label}</span>
              </div>
            `;
          }
        },
        itemMargin: { horizontal: 15, vertical: 8 },
      },
      dataLabels: { enabled: false },
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              name: { show: false },
              value: { show: true, fontSize: "24px", fontWeight: 600 },
              total: {
                show: true,
                label: "Total",
                fontSize: "18px",
                fontWeight: 600,
                formatter: () => totalValue,
              },
            },
          },
        },
      },
      responsive: [
        {
          breakpoint: 768,
          options: { chart: { width: "100%", height: "300" }, legend: { position: "bottom" } },
        },
        {
          breakpoint: 480,
          options: { chart: { width: "100%", height: "250" }, legend: { position: "bottom" } },
        },
      ],
    };

    if (!chartRef.current) return;

    const chart = new ApexCharts(chartRef.current, options);
    chart.render();
    let legendHoverCleanup = null;

    // SINGLE right-click handler using mousedown
    const handleRightClick = (e) => {
      if (e.button !== 2) return; // Only handle right mouse button
      if (!onDataPointRightClick) return;
      
      // Prevent multiple calls with ref
      if (rightClickHandledRef.current) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      
      rightClickHandledRef.current = true;
      setTimeout(() => {
        rightClickHandledRef.current = false;
      }, 500);
      
      e.preventDefault();
      e.stopPropagation();
      
      const target = e.target;
      if (!target || !chartRef.current) return;
      
      let sliceIndex = -1;
      
      // Check if clicked on a legend item
      const legendItem = target.closest ? target.closest('.apexcharts-legend-series') : null;
      if (legendItem) {
        try {
          const allLegendItems = chartRef.current.querySelectorAll('.apexcharts-legend-series');
          sliceIndex = Array.from(allLegendItems).findIndex(item => item === legendItem);
        } catch (e) {
          // Ignore legend click errors
        }
      }
      
      // Check if clicked on a pie slice
      if (sliceIndex === -1) {
        const clickedElement = target.closest ? target.closest('.apexcharts-pie-area, .apexcharts-pie-slice, path[class*="apexcharts-pie"]') : target;
        
        if (clickedElement && clickedElement.tagName === 'path') {
          // Try to get from data attribute
          try {
            if (clickedElement.getAttribute) {
              const realIndex = clickedElement.getAttribute('data:realIndex');
              if (realIndex !== null && realIndex !== '') {
                sliceIndex = parseInt(realIndex, 10);
              }
            }
          } catch (e) {
            // Ignore attribute access errors
          }
          
          // Find by position in DOM
          if (sliceIndex === -1 || isNaN(sliceIndex)) {
            try {
              const allPaths = chartRef.current.querySelectorAll('path[class*="apexcharts-pie"]');
              sliceIndex = Array.from(allPaths).findIndex(path => path === clickedElement);
            } catch (e) {
              // Ignore query errors
            }
          }
          
          // Try to find by checking all SVG paths in the pie area
          if (sliceIndex === -1 || isNaN(sliceIndex)) {
            try {
              const pieArea = chartRef.current.querySelector('.apexcharts-pie');
              if (pieArea) {
                const allPaths = pieArea.querySelectorAll('path');
                sliceIndex = Array.from(allPaths).findIndex(path => path === clickedElement);
              }
            } catch (e) {
              // Ignore query errors
            }
          }
        }
      }
      
      // Execute callback if valid index found
      if (sliceIndex >= 0 && sliceIndex < labels.length && labels[sliceIndex]) {
        onDataPointRightClick(labels[sliceIndex], sliceIndex, series[sliceIndex]);
      }
    };

    // Attach right-click handler
    if (onDataPointRightClick && chartRef.current) {
      chartRef.current.addEventListener('mousedown', handleRightClick, true);
    }

    // Ensure legend hover updates donut center value consistently.
    const setupLegendHoverValueSync = () => {
      if (!chartRef.current) return false;
      const chartElement = chartRef.current;
      const legendItems = chartElement.querySelectorAll('.apexcharts-legend .apexcharts-legend-series');
      const centerValueNode = chartElement.querySelector('.apexcharts-datalabel-value');

      if (!legendItems || legendItems.length === 0 || !centerValueNode) {
        return false;
      }

      const listeners = [];
      legendItems.forEach((item, index) => {
        const onMouseEnter = () => {
          if (index >= 0 && index < series.length) {
            centerValueNode.textContent = `${series[index]}`;
          }
        };
        const onMouseLeave = () => {
          centerValueNode.textContent = `${totalValue}`;
        };

        item.addEventListener('mouseenter', onMouseEnter);
        item.addEventListener('mouseleave', onMouseLeave);
        listeners.push(() => {
          item.removeEventListener('mouseenter', onMouseEnter);
          item.removeEventListener('mouseleave', onMouseLeave);
        });
      });

      legendHoverCleanup = () => {
        listeners.forEach((dispose) => dispose());
      };
      return true;
    };

    // Add cursor styling to chart slices and legend after chart is rendered
    let isMounted = true;
    let timeoutIds = [];
    
    if (onDataPointClick) {
      const addCursorStyling = () => {
        // Check if component is still mounted and chart ref is valid
        if (!isMounted || !chartRef.current) return false;
        
        try {
          const chartElement = chartRef.current;
          if (!chartElement || typeof chartElement.querySelector !== 'function') return false;
          
          // Add cursor pointer to chart slices for better UX
          try {
            const slices = chartElement.querySelectorAll('.apexcharts-pie-area, .apexcharts-pie-slice');
            if (slices && slices.length > 0) {
              slices.forEach((slice) => {
                try {
                  if (slice && slice.style) {
                    slice.style.cursor = 'pointer';
                  }
                } catch (e) {
                  // Ignore errors on individual slices
                }
              });
            }
          } catch (e) {
            // Slices might not be ready yet
          }
          
          // Add cursor pointer to legend items
          try {
            const legendItems = chartElement.querySelectorAll('.apexcharts-legend .apexcharts-legend-series');
            if (legendItems && legendItems.length > 0) {
              legendItems.forEach((item) => {
                try {
                  if (item && item.style) {
                    item.style.cursor = 'pointer';
                  }
                } catch (e) {
                  // Ignore errors on individual legend items
                }
              });
            }
          } catch (e) {
            // Legend might not be ready yet
          }
          
          return true;
        } catch (error) {
          console.warn('Error adding cursor styling to DonutChart:', error);
          return false;
        }
      };
      
      // Try to add styling with retries
      let attempts = 0;
      const maxAttempts = 10;
      const tryAddStyling = () => {
        if (!isMounted) return;
        try {
          if (addCursorStyling() || attempts >= maxAttempts) {
            return;
          }
          attempts++;
          const timeoutId = setTimeout(tryAddStyling, 200);
          timeoutIds.push(timeoutId);
        } catch (error) {
          console.warn('Error in tryAddStyling:', error);
        }
      };
      
      // Start after chart is rendered
      try {
        // Try now and retry in case legend is mounted after animation.
        setupLegendHoverValueSync();
        const timeoutId1 = setTimeout(tryAddStyling, 300);
        timeoutIds.push(timeoutId1);
        
        // Also try after chart animation completes
        const timeoutId2 = setTimeout(tryAddStyling, 1200);
        timeoutIds.push(timeoutId2);
        const timeoutId3 = setTimeout(setupLegendHoverValueSync, 400);
        timeoutIds.push(timeoutId3);
        const timeoutId4 = setTimeout(setupLegendHoverValueSync, 1200);
        timeoutIds.push(timeoutId4);
      } catch (error) {
        console.warn('Error setting up DonutChart timeouts:', error);
      }
    } else {
      // Even without click handling, keep legend hover value sync enabled.
      try {
        setupLegendHoverValueSync();
        const timeoutId1 = setTimeout(setupLegendHoverValueSync, 400);
        timeoutIds.push(timeoutId1);
        const timeoutId2 = setTimeout(setupLegendHoverValueSync, 1200);
        timeoutIds.push(timeoutId2);
      } catch (error) {
        console.warn('Error setting up legend hover sync:', error);
      }
    }

    return () => {
      isMounted = false;
      // Clear all timeouts
      timeoutIds.forEach(id => clearTimeout(id));
      timeoutIds = [];
      // Remove right-click handler
      if (chartRef.current) {
        chartRef.current.removeEventListener('mousedown', handleRightClick, true);
      }
      if (legendHoverCleanup) {
        legendHoverCleanup();
      }
      // Destroy chart
      if (chart) {
        try {
          chart.destroy();
        } catch (error) {
          console.warn('Error destroying DonutChart:', error);
        }
      }
    };
  }, [series, labels, width, height, legendPosition, type, onDataPointClick, onDataPointRightClick]);

  // Handle clicks via event delegation on the chart container
  const handleChartClick = (e) => {
    if (!onDataPointClick || !chartRef.current) return;
    
    // Prevent ApexCharts from processing the click internally
    e.stopPropagation();
    
    try {
      const target = e.target;
      if (!target) return;
      
      const chartElement = chartRef.current;
      if (!chartElement || typeof chartElement.querySelector !== 'function') return;
      
      // Check if click is on a legend item
      const legendItem = target.closest ? target.closest('.apexcharts-legend-series') : null;
      if (legendItem) {
        try {
          // Find the legend item's index by checking all legend items
          const allLegendItems = chartElement.querySelectorAll('.apexcharts-legend-series');
          const legendIndex = Array.from(allLegendItems).findIndex(item => item === legendItem);
          
          if (legendIndex >= 0 && legendIndex < labels.length && labels[legendIndex]) {
            onDataPointClick(labels[legendIndex], legendIndex, series[legendIndex]);
            return;
          }
        } catch (e) {
          // Ignore legend click errors, fall through to pie slice handling
        }
      }
      
      // Check if click is on a pie slice or path element
      const clickedElement = target.closest ? target.closest('.apexcharts-pie-area, .apexcharts-pie-slice, path[class*="apexcharts-pie"]') : target;
      
      if (clickedElement && clickedElement.tagName === 'path') {
        // Try to get the index from the path's data attribute or by finding it in the list
        let sliceIndex = -1;
        
        // Method 1: Try to get from data attribute (ApexCharts stores index here)
        try {
          if (clickedElement.getAttribute) {
            const realIndex = clickedElement.getAttribute('data:realIndex');
            if (realIndex !== null && realIndex !== '') {
              sliceIndex = parseInt(realIndex, 10);
            }
          }
        } catch (e) {
          // Ignore attribute access errors
        }
        
        // Method 2: Find by position in DOM (find all pie paths and match)
        if (sliceIndex === -1 || isNaN(sliceIndex)) {
          try {
            const allPaths = chartElement.querySelectorAll('path[class*="apexcharts-pie"]');
            sliceIndex = Array.from(allPaths).findIndex(path => path === clickedElement);
          } catch (e) {
            // Ignore query errors
          }
        }
        
        // Method 3: Try to find by checking all SVG paths in the pie area
        if (sliceIndex === -1 || isNaN(sliceIndex)) {
          try {
            const pieArea = chartElement.querySelector('.apexcharts-pie');
            if (pieArea) {
              const allPaths = pieArea.querySelectorAll('path');
              sliceIndex = Array.from(allPaths).findIndex(path => path === clickedElement);
            }
          } catch (e) {
            // Ignore query errors
          }
        }
        
        if (sliceIndex >= 0 && sliceIndex < labels.length && labels[sliceIndex]) {
          onDataPointClick(labels[sliceIndex], sliceIndex, series[sliceIndex]);
        }
      }
    } catch (error) {
      console.warn('Error handling DonutChart click:', error);
    }
  };

  return (
    <div 
      ref={chartRef} 
      className="donut-chart-wrapper"
      style={{ cursor: onDataPointClick ? 'pointer' : 'default' }}
      onClick={onDataPointClick ? handleChartClick : undefined}
    />
  );
}
